import { TZDate } from '@date-fns/tz';
import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
} from 'date-fns';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import type { FinancesBucketGranularity } from './apiHelpers';

export enum DateFilterOption {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last7Days',
  Last30Days = 'last30Days',
  MonthToDate = 'monthToDate',
  QuarterToDate = 'quarterToDate',
  YearToDate = 'yearToDate',
  CustomRange = 'customRange',
}

export interface FinancesDateRange {
  startDate: string;
  endDate: string;
}

export interface DateFilterOptionConfig {
  value: DateFilterOption;
  label: string;
}

export const DATE_FILTER_GROUPS: DateFilterOptionConfig[][] = [
  [
    { value: DateFilterOption.Today, label: 'Today' },
    { value: DateFilterOption.Yesterday, label: 'Yesterday' },
  ],
  [
    { value: DateFilterOption.Last7Days, label: 'Last 7 days' },
    { value: DateFilterOption.Last30Days, label: 'Last 30 days' },
    { value: DateFilterOption.MonthToDate, label: 'Month to date' },
    { value: DateFilterOption.QuarterToDate, label: 'Quarter to date' },
    { value: DateFilterOption.YearToDate, label: 'Year to date' },
  ],
  [{ value: DateFilterOption.CustomRange, label: 'Custom range...' }],
];

export const DATE_FILTER_OPTIONS: DateFilterOptionConfig[] =
  DATE_FILTER_GROUPS.flat();

export const getDateFilterLabel = (filter: DateFilterOption): string =>
  DATE_FILTER_OPTIONS.find((option) => option.value === filter)?.label ??
  'Today';

interface GetFinancesDateRangeArgs {
  filter: DateFilterOption;
  timezone: string;
  customRange?: FinancesDateRange;
}

export const getFinancesDateRange = ({
  filter,
  timezone,
  customRange,
}: GetFinancesDateRangeArgs): FinancesDateRange => {
  const today = TZDate.tz(timezone);

  if (filter === DateFilterOption.CustomRange && customRange) {
    return customRange;
  }

  switch (filter) {
    case DateFilterOption.Yesterday: {
      const yesterday = subDays(today, 1);
      const isoDate = toISODateFormat(yesterday);
      return { startDate: isoDate, endDate: isoDate };
    }
    case DateFilterOption.Last7Days:
      return {
        startDate: toISODateFormat(subDays(today, 6)),
        endDate: toISODateFormat(today),
      };
    case DateFilterOption.Last30Days:
      return {
        startDate: toISODateFormat(subDays(today, 29)),
        endDate: toISODateFormat(today),
      };
    case DateFilterOption.MonthToDate:
      return {
        startDate: toISODateFormat(startOfMonth(today)),
        endDate: toISODateFormat(endOfMonth(today)),
      };
    case DateFilterOption.QuarterToDate:
      return {
        startDate: toISODateFormat(startOfQuarter(today)),
        endDate: toISODateFormat(endOfQuarter(today)),
      };
    case DateFilterOption.YearToDate:
      return {
        startDate: toISODateFormat(startOfYear(today)),
        endDate: toISODateFormat(endOfYear(today)),
      };
    case DateFilterOption.Today:
    default:
      return {
        startDate: toISODateFormat(today),
        endDate: toISODateFormat(today),
      };
  }
};

export const getEarningsChartTitle = (
  granularity: FinancesBucketGranularity | undefined,
): string => {
  switch (granularity) {
    case 'day':
      return 'Earnings by day';
    case 'month':
      return 'Earnings by month';
    default:
      return 'Earnings';
  }
};
